import * as Sentry from "@sentry/react";
import type { CreateSchoolClassOptions, SilentErrorOptions } from "p6m-contexts";
import type { SchoolClass } from "p6m-p6u";
import type {
    InviteModalState,
    NewClassFormState,
} from "../complex/classActivityCard/ClassActivityCardsOverview.types";
import { ClassActivityCategory } from "../constants/ClassActivityConstants";
import { NO_CLASS_SELECTION, ShareModes } from "../enums/shareFlow";
import { fetchClassMemberCount } from "../networking/schoolClasses";
import { SchoolClassVisibilities } from "../enums/schoolClasses";
import { isPrivateSchoolClass } from "../utils/schoolClassUtils";

// Tests are evaluated per student, so they always need a private class — vocab trainings and practice mode
// don't. A not-yet-chosen mode (null) conservatively counts as requiring one, so callers passing a nullable
// mode must decide whether that fail-closed default is what they want.
export const requiresPrivateClass = (category: ClassActivityCategory, mode: ShareModes | null): boolean =>
    category === ClassActivityCategory.TEST && mode !== ShareModes.PRACTICE;

export const allowsNoClassSelection = (category: ClassActivityCategory, mode: ShareModes | null): boolean =>
    !requiresPrivateClass(category, mode);

export const resolveSelectedSchoolClassId = (selectedSchoolClassId: string | null): string | null =>
    selectedSchoolClassId === NO_CLASS_SELECTION ? null : selectedSchoolClassId;

export const hasEffectiveClass = (
    selectedSchoolClassId: string | null,
    newClassForm: NewClassFormState | null
): boolean => resolveSelectedSchoolClassId(selectedSchoolClassId) !== null || !!newClassForm?.isCommitted;

interface ResolveEmptyClassInviteModalParams {
    effectiveSchoolClassId: string | null; //existing selection, or one just created for this share
    createdClassName?: string; // only present for classes created in this share, not in `schoolClasses` yet
    schoolClasses: SchoolClass[];
}

// A freshly created class is private + empty by definition, so skip the member lookup for it.
// A null count (lookup failed) counts as "has members" so a failed lookup doesn't nag a full class.
export const resolveEmptyClassInviteModal = async (
    params: ResolveEmptyClassInviteModalParams
): Promise<InviteModalState | null> => {
    const schoolClassId = params.effectiveSchoolClassId;
    if (!schoolClassId) return null;

    if (params.createdClassName !== undefined) {
        return { schoolClassId, schoolClassName: params.createdClassName };
    }

    const schoolClass = params.schoolClasses.find((c) => c.token === schoolClassId);
    if (!schoolClass || !isPrivateSchoolClass(schoolClass.classVisibility)) return null;

    const memberCount = await fetchClassMemberCount(schoolClassId);
    if (memberCount !== 0) return null;

    return { schoolClassId, schoolClassName: schoolClass.name };
};

// A committed new-class draft always counts (it's created private); an existing selection only counts if it
// resolves to a PRIVATE class. Unknown tokens — class list still loading or failed to load — count as no
// class, so a stale selection can't slip a test share past the private-class requirement.
export const hasEffectivePrivateClass = (
    selectedSchoolClassId: string | null,
    newClassForm: NewClassFormState | null,
    schoolClasses: SchoolClass[]
): boolean => {
    if (newClassForm?.isCommitted) return true;
    const resolvedSchoolClassId = resolveSelectedSchoolClassId(selectedSchoolClassId);
    const selectedClass = schoolClasses.find((c) => c.token === resolvedSchoolClassId);
    return isPrivateSchoolClass(selectedClass?.classVisibility);
};

// Collapse the inline form. Forward Next on a valid open form commits the draft and snapshots the name.
// Any other collapse (Back, "Select class" row) reverts unconfirmed edits to the last committed name.
export const collapseClassForm = (form: NewClassFormState, committing: boolean): NewClassFormState => {
    if (committing) return { ...form, isOpen: false, isCommitted: true, committedName: form.name };
    const revertedName = form.isCommitted && form.committedName !== null ? form.committedName : form.name;
    return { ...form, isOpen: false, name: revertedName };
};

export enum ShareWithClassRollbackStages {
    CREATE = "CREATE",
    UPDATE = "UPDATE",
    SHARE = "SHARE",
}

export interface ShareWithClassRollbackParams {
    activityId: string;
    selectedSchoolClassId: string | null;
    previousSchoolClassId: string | null;
    newClassForm: NewClassFormState | null;
    createSchoolClass: (
        name: string,
        visibility: SchoolClassVisibilities,
        options: CreateSchoolClassOptions
    ) => Promise<string | null>;
    deleteSchoolClass: (schoolClassId: string, options: SilentErrorOptions) => Promise<boolean>;
    // Resolves false when the assignment did not land. The rollback gates the created-class delete on this,
    // so an implementation that cannot confirm the write must not report success.
    updateActivitySchoolClass: (
        activityId: string,
        schoolClassId: string | null,
        options: SilentErrorOptions
    ) => Promise<boolean>;
    shareActivity: (effectiveSchoolClassId: string | null) => Promise<unknown>;
    getErrorMessage: (params: { stage: ShareWithClassRollbackStages; rolledBack: boolean }) => string;
}

export type ShareWithClassRollbackResult =
    | { ok: true; effectiveSchoolClassId: string | null; createdSchoolClassId: string | null }
    | { ok: false; errorMessage: string };

// Run the create-class → assign-class → share-activity chain with rollback semantics shared by the test
// and vocab-training share flows. On failure: restore the prior class assignment if we changed it, and
// delete the freshly created class so a half-finished share doesn't leave dangling backend state.
export const executeShareWithClassRollback = async (
    params: ShareWithClassRollbackParams
): Promise<ShareWithClassRollbackResult> => {
    let createdSchoolClassId: string | null = null;
    let stage: ShareWithClassRollbackStages = ShareWithClassRollbackStages.CREATE;
    let assignmentChanged = false;

    try {
        let effectiveSchoolClassId: string | null;
        if (params.newClassForm?.isCommitted) {
            const name = params.newClassForm.name.trim();
            // suppressCreatedEvent: the class may be rolled back below, so the callers emit
            // "Created Class" only after the whole share has succeeded. Known gap: if the share
            // fails AND the rollback delete also fails, the class survives un-counted.
            createdSchoolClassId = await params.createSchoolClass(name, SchoolClassVisibilities.PRIVATE, {
                silentOnError: true,
                suppressCreatedEvent: true,
            });
            if (!createdSchoolClassId) throw new Error("create-class-failed");
            effectiveSchoolClassId = createdSchoolClassId;
        } else {
            effectiveSchoolClassId = resolveSelectedSchoolClassId(params.selectedSchoolClassId);
        }

        if (effectiveSchoolClassId !== params.previousSchoolClassId) {
            stage = ShareWithClassRollbackStages.UPDATE;
            await params.updateActivitySchoolClass(params.activityId, effectiveSchoolClassId, { silentOnError: true });
            assignmentChanged = true;
        }

        stage = ShareWithClassRollbackStages.SHARE;
        await params.shareActivity(effectiveSchoolClassId);

        return { ok: true, effectiveSchoolClassId, createdSchoolClassId };
    } catch (error) {
        // The flow swallows the error into a user-facing message, so report it here — share
        // failures are otherwise invisible in Sentry.
        Sentry.captureException(error, {
            extra: { stage, activityId: params.activityId },
        });

        // Nothing was assigned to the created class unless the UPDATE stage ran, so it starts off it.
        let isActivityRemovedFromCreatedClass = true;
        if (assignmentChanged) {
            try {
                isActivityRemovedFromCreatedClass = await params.updateActivitySchoolClass(
                    params.activityId,
                    params.previousSchoolClassId,
                    { silentOnError: true }
                );
            } catch {
                isActivityRemovedFromCreatedClass = false;
            }
            if (!isActivityRemovedFromCreatedClass) {
                console.warn("Failed to restore previous class assignment on share rollback", {
                    activityId: params.activityId,
                    previousSchoolClassId: params.previousSchoolClassId,
                });
            }
        }

        // Deleting a class cascade-deletes its activities, so a created class may only be cleaned up once the
        // activity is verifiably off it. An orphaned empty class is recoverable; the activity is not.
        const rolledBack =
            !!createdSchoolClassId &&
            isActivityRemovedFromCreatedClass &&
            (await params.deleteSchoolClass(createdSchoolClassId, { silentOnError: true }));

        return { ok: false, errorMessage: params.getErrorMessage({ stage, rolledBack }) };
    }
};
